import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import appRoutes from "./routes";
import "./index.css";
import React from "react";
import { PageTitleProvider } from "./context/PageTitleContext";
import { datadogRum } from "@datadog/browser-rum";
import { ErrorBoundary, reactPlugin } from "@datadog/browser-rum-react";
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v7";
import AppCrashFallback from "./components/AppCrashFallback/AppCrashFallback";
import { isRetryableError } from "./helper/errors";
import { reportError } from "./helper/reportError";

datadogRum.init({
  applicationId: "001bb172-6ef7-4df3-a38a-2bcb64f03f28",
  clientToken: "pub9cddb912b18b58fbaf102ed59c31b7a7",
  site: "datadoghq.eu",
  service: "hence-global-app",
  env: import.meta.env.MODE,
  trackUserInteractions: true,
  trackResources: true,

  // Specify a version number to identify the deployed version of your application in Datadog
  version: __GIT_COMMIT_HASH__ + (__GIT_DIRTY__ ? "-dirty" : ""),
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  defaultPrivacyLevel: "mask-user-input",
  plugins: [reactPlugin({ router: true })],
});

const MAX_QUERY_RETRIES = 3;

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      reportError(error, { queryKey: query.queryKey });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reportError(error, { mutationKey: mutation.options.mutationKey });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      // Retrying a 4xx only repeats the rejection while the UI sits on a
      // skeleton, delaying the moment the user learns what actually happened.
      retry: (failureCount, error) =>
        failureCount < MAX_QUERY_RETRIES && isRetryableError(error),
    },
  },
});

const router = createBrowserRouter(appRoutes);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary fallback={AppCrashFallback}>
      <QueryClientProvider client={queryClient}>
        <PageTitleProvider>
          <RouterProvider router={router} />
        </PageTitleProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
